import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import {
  ICustomerEvetFilterParams,
  ICustomerRecognitionEventSimpleProps,
} from '../../../services/Events/CustomerRecognitionEventService/types';
import { IPaginationParams } from '../../../services/types';
import { useGetAllCustomerEventByLicenseCountAsync } from '../../../services/Events/CustomerRecognitionEventService/hooks/useMutation';
import {
  CUSTOMER_RECOGNITION_EVENT_CACHE_KEY,
  useCustomerRecognitionEventDataStreamCache,
} from '../../../services/Events/CustomerRecognitionEventService/hooks/useQueryClient';
import { IEventPauseUpdateParams } from '../../../services/Events/types';
import { getStreamByEndpointAsync } from '../../../services/Events';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import PauseButtonComponent from '../../../components/PauseButtonComponent';
import ImageModalComponent from '../../../components/ImageModalComponent';
import { CUSTOMER_RECOGNITION_EVENT_ENDPOINT } from '../../../services/Events/CustomerRecognitionEventService';
import ImageSimpleColumnComponent from '../../../components/ImageSimpleColumnComponent';
import dayjs from 'dayjs';
import { getCustomerGenderType } from '../../../converters/Recognition/Customer';
import {
  CustomerGenderTypes,
  ICustomerRecognitionProps,
} from '../../../services/Recognition/Customer/types';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import { PersonSearch } from '@mui/icons-material';
import { useGetCustomerRecognitionByIdAsync } from '../../../services/Recognition/Customer/hooks/useMutation';
import CustomerDetailsModal from './CustomerDetailsModal';
import { t } from 'i18next';

const CustomerMonitoringListPage = () => {
  const { setIsShowLoadingComponent } = useContext(AppContext);
  const [events, setEvents] = useState<ICustomerRecognitionEventSimpleProps[]>(
    [],
  );
  const [eventsCount, setEventsCount] = useState<number>();
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [filter, setFilter] = useState<ICustomerEvetFilterParams>({
    search: '',
  });
  const [isOpenImageModal, setIsOpenImageModal] = useState<boolean>(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState<boolean>(false);
  const [selectedRecognition, setSelectedRecognition] =
    useState<ICustomerRecognitionProps>();
  const { mutateAsync: mutateEventCount, isLoading: isLoadingEventCount } =
    useGetAllCustomerEventByLicenseCountAsync();
  const { setCacheData, getCacheData, clearCacheData } =
    useCustomerRecognitionEventDataStreamCache();
  const [pausedUpdateParams, setPausedUpdateParams] =
    useState<IEventPauseUpdateParams>({
      isPaused: false,
      cacheKey: CUSTOMER_RECOGNITION_EVENT_CACHE_KEY,
      setCacheData: setCacheData,
      getCacheData: getCacheData,
      clearCacheData: clearCacheData,
    });
  const { mutateAsync: mutateGetRecognition } =
    useGetCustomerRecognitionByIdAsync();

  const columns: IColumnProps[] = [
    {
      id: 'customerRecognition.name',
      label: t('common.name'),
      width: 'auto',
    },
    {
      id: 'customerRecognition.gender',
      label: t('common.gender'),
      width: 'auto',
      render: (gender: string) => {
        return <>{getCustomerGenderType(gender as CustomerGenderTypes)}</>;
      },
    },
    {
      id: 'customerRecognition.birthDate',
      label: t('customerMonitoring.birthday'),
      width: 'auto',
      render: (datetime: string) => {
        return <>{formatDateTime(datetime, 'DD/MM/YYYY')}</>;
      },
    },
    {
      id: 'createdAt',
      label: t('common.date'),
      width: 'auto',
      render: (datetime: string) => {
        return <>{formatDateTime(datetime)}</>;
      },
    },
    {
      id: 'recognitionImage',
      label: t('common.recognitionImage'),
      align: 'center',
      width: '0px',
      render: (url: string) => {
        return (
          <ImageSimpleColumnComponent
            url={url}
            onClick={() => handleImageModal(url)}
          />
        );
      },
    },
    {
      id: 'customerRecognition.image',
      label: t('common.recognitionImage'),
      align: 'center',
      width: '0px',
      render: (url: string) => {
        return (
          <ImageSimpleColumnComponent
            url={url}
            onClick={() => handleImageModal(url)}
          />
        );
      },
    },
    {
      id: 'customerRecognition.id',
      label: t('common.actions'),
      align: 'center',
      width: '0px',
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          actionOptions={actionOptions(Number(id))}
        />
      ),
    },
  ];

  const actionOptions = (id: number) => {
    return [
      {
        label: 'Informações',
        icon: <PersonSearch />,
        onClick: () => {
          handleDetailsModal(id);
        },
      },
    ];
  };

  const handleDetailsModal = async (id: number) => {
    setIsShowLoadingComponent(true);
    await mutateGetRecognition(id)
      .then((response) => {
        setSelectedRecognition(response);
        setIsOpenDetailsModal(true);
      })
      .finally(() => setIsShowLoadingComponent(false));
  };

  const handleCloseDetailsModal = () => {
    setSelectedRecognition(undefined);
    setIsOpenDetailsModal(false);
  };

  const formatDateTime = (datetime: string, format?: string) => {
    return dayjs(datetime).format(format || 'DD/MM/YYYY HH:mm');
  };

  const handleImageModal = (url: string) => {
    setSelectedImageUrl(url);
    setIsOpenImageModal(true);
  };

  const onResponseStream = async () => {
    await mutateEventCount(filter)
      .then(setEventsCount)
      .finally(() => setIsShowLoadingComponent(false));
  };

  const refetchEvents = async () => {
    await getStreamByEndpointAsync(
      CUSTOMER_RECOGNITION_EVENT_ENDPOINT,
      setEvents,
      pagination,
      onResponseStream,
      setIsShowLoadingComponent,
      pausedUpdateParams,
    ).finally(() => setIsShowLoadingComponent(false));
  };

  useEffect(() => {
    setIsShowLoadingComponent(true);
    refetchEvents();
  }, [pagination, pausedUpdateParams.isPaused]);

  return (
    <>
      <TableComponent
        rows={events}
        columns={columns}
        totalCount={eventsCount}
        onChangePageOrRowsPerPage={setPagination}
        additionalTitleComponent={
          <PauseButtonComponent
            pausedUpdateParams={pausedUpdateParams}
            setPausedUpdateParams={setPausedUpdateParams}
          />
        }
        title={t('common.lastEvent')}
      />
      <ImageModalComponent
        isOpen={isOpenImageModal}
        handleClose={() => setIsOpenImageModal(false)}
        imageUrl={selectedImageUrl}
      />
      <CustomerDetailsModal
        selectedRecognition={selectedRecognition}
        isOpen={isOpenDetailsModal}
        setIsOpen={setIsOpenDetailsModal}
        onClose={handleCloseDetailsModal}
      />
    </>
  );
};

export default CustomerMonitoringListPage;
